/**
 * The compliance simulator (plan E05-T05): describe a call, see what the rules decide and why. The
 * dry run uses the tenant's own settings and looks up the number's consents, do-not-call lists and
 * recent attempts as a real call would; nothing is dialled.
 */
import { useMutation } from "@tanstack/react-query";
import { useId, useState, type FormEvent } from "react";
import { data, type Schemas } from "../api/client";
import { useApiQuery } from "../app/Page";
import { useBff } from "../app/session";
import { useFormat, useT, type MessageId } from "../i18n";
import { Button } from "../ui/Button";
import { ErrorState } from "../ui/states";
import { sourcesOf } from "./files";
import { SourceList } from "./Rules";

type Decision = Schemas["Decision"];
type Point = Schemas["EvaluateIn"]["point"];
const POINTS: Point[] = ["outbound.originate", "inbound.accept", "action.transfer", "recording.start", "voicemail.drop"];
const LEGS = ["pstn", "sip", "internal"] as const;
const CAMPAIGN_TYPES = ["promotional", "service", "service_regulated", "transactional", "transactional_regulated", "debt_collection"];

const field = "w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm";

export function Simulator() {
  const t = useT();
  const { api } = useBff();
  const id = useId();
  const [point, setPoint] = useState<Point>("outbound.originate");
  const [to, setTo] = useState("");
  const [from, setFrom] = useState("");
  const [campaign, setCampaign] = useState("");
  const [when, setWhen] = useState("");
  const [language, setLanguage] = useState("en");
  const [recording, setRecording] = useState(false);
  const [legs, setLegs] = useState<{ from: (typeof LEGS)[number]; to: (typeof LEGS)[number] }>({ from: "pstn", to: "pstn" });
  const [fields, setFields] = useState("");
  const fieldsProblem = (() => {
    if (!fields.trim()) return null;
    try {
      const parsed: unknown = JSON.parse(fields);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? null : t("compliance.sim.fieldsObject");
    } catch {
      return t("compliance.sim.fieldsJson");
    }
  })();

  const run = useMutation({
    mutationFn: () => {
      const context: Schemas["CallContext"] = {
        direction: point === "inbound.accept" ? "inbound" : "outbound",
        to_e164: to || null,
        from_e164: from || null,
        campaign_type: campaign || null,
        language,
        recording: recording || point === "recording.start",
        caller_id_owned: true, // the API checks ownership itself when it places a call
        bridging_attested: false, // the tenant's own attestation still applies
        ...(when ? { now: new Date(when).toISOString() } : {}),
        ...(point === "action.transfer" ? { from_leg: legs.from, to_leg: legs.to } : {}),
        ...(fields.trim() ? { campaign_fields: JSON.parse(fields) as Record<string, unknown> } : {}),
      };
      return data(api.POST("/v1/compliance/evaluate", { body: { point, context } }));
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!fieldsProblem) run.mutate();
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
      <form onSubmit={submit} className="space-y-3" aria-describedby={`${id}-intro`}>
        <p id={`${id}-intro`} className="text-sm text-[var(--color-muted)]">{t("compliance.sim.intro")}</p>
        <div className="space-y-1">
          <label htmlFor={`${id}-point`} className="block text-sm font-medium">{t("compliance.sim.point")}</label>
          <select id={`${id}-point`} className={field} value={point} onChange={(e) => setPoint(e.target.value as Point)}>
            {POINTS.map((p) => <option key={p} value={p}>{t(`compliance.point.${p}` as MessageId)}</option>)}
          </select>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label htmlFor={`${id}-to`} className="block text-sm font-medium">{t("compliance.sim.to")}</label>
            <input id={`${id}-to`} className={`${field} font-mono`} value={to} placeholder="+919876543210"
              onChange={(e) => setTo(e.target.value.trim())} />
          </div>
          <div className="space-y-1">
            <label htmlFor={`${id}-from`} className="block text-sm font-medium">{t("compliance.sim.from")}</label>
            <input id={`${id}-from`} className={`${field} font-mono`} value={from} placeholder="+911401234567"
              onChange={(e) => setFrom(e.target.value.trim())} />
          </div>
        </div>
        <div className="space-y-1">
          <label htmlFor={`${id}-campaign`} className="block text-sm font-medium">{t("compliance.sim.campaign")}</label>
          <input id={`${id}-campaign`} className={field} list={`${id}-types`} value={campaign}
            aria-describedby={`${id}-campaign-hint`} onChange={(e) => setCampaign(e.target.value.trim())} />
          <datalist id={`${id}-types`}>{CAMPAIGN_TYPES.map((c) => <option key={c} value={c} />)}</datalist>
          <p id={`${id}-campaign-hint`} className="text-xs text-[var(--color-muted)]">{t("compliance.sim.campaignHint")}</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label htmlFor={`${id}-when`} className="block text-sm font-medium">{t("compliance.sim.when")}</label>
            <input id={`${id}-when`} type="datetime-local" className={field} value={when}
              aria-describedby={`${id}-when-hint`} onChange={(e) => setWhen(e.target.value)} />
            <p id={`${id}-when-hint`} className="text-xs text-[var(--color-muted)]">{t("compliance.sim.whenHint")}</p>
          </div>
          <div className="space-y-1">
            <label htmlFor={`${id}-language`} className="block text-sm font-medium">{t("compliance.sim.language")}</label>
            <input id={`${id}-language`} className={field} value={language} onChange={(e) => setLanguage(e.target.value.trim() || "en")} />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={recording} onChange={(e) => setRecording(e.target.checked)} />
          {t("compliance.sim.recording")}
        </label>
        {point === "action.transfer" && (
          <fieldset className="grid gap-3 sm:grid-cols-2">
            <legend className="mb-1 text-sm font-medium">{t("compliance.sim.legs")}</legend>
            {(["from", "to"] as const).map((side) => (
              <div key={side} className="space-y-1">
                <label htmlFor={`${id}-leg-${side}`} className="block text-sm">{t(`compliance.sim.leg.${side}`)}</label>
                <select id={`${id}-leg-${side}`} className={field} value={legs[side]}
                  onChange={(e) => setLegs({ ...legs, [side]: e.target.value as (typeof LEGS)[number] })}>
                  {LEGS.map((l) => <option key={l} value={l}>{t(`compliance.leg.${l}`)}</option>)}
                </select>
              </div>
            ))}
          </fieldset>
        )}
        <div className="space-y-1">
          <label htmlFor={`${id}-fields`} className="block text-sm font-medium">{t("compliance.sim.fields")}</label>
          <textarea id={`${id}-fields`} className={`${field} h-24 font-mono`} value={fields}
            placeholder='{"dlt_pe_id": "1201…", "purpose": "a reminder"}'
            aria-invalid={fieldsProblem ? true : undefined}
            aria-describedby={`${id}-fields-hint`} onChange={(e) => setFields(e.target.value)} />
          <p id={`${id}-fields-hint`} className={`text-xs ${fieldsProblem ? "text-[var(--color-danger)]" : "text-[var(--color-muted)]"}`}>
            {fieldsProblem ?? t("compliance.sim.fieldsHint")}
          </p>
        </div>
        <Button type="submit" disabled={run.isPending || Boolean(fieldsProblem)}>{t("compliance.sim.run")}</Button>
      </form>
      <div aria-live="polite">
        {run.isError && <ErrorState error={run.error} />}
        {run.data && <Outcome decision={run.data} />}
      </div>
    </div>
  );
}

function Outcome({ decision }: { decision: Decision }) {
  const t = useT();
  const format = useFormat();
  const profile = decision.profile;
  const effective = useApiQuery(`compliance-effective:${profile.id}`, (api) =>
    api.GET("/v1/compliance/effective", { params: { query: { profile: profile.id } } }),
  );
  const sources = effective.data?.profile.sources;
  const tone = decision.outcome === "allow" ? "border-[var(--color-success)]" : decision.outcome === "defer"
    ? "border-[var(--color-warning)]" : "border-[var(--color-danger)]";
  const plays = decision.obligations?.play ?? [];
  const retention = Object.entries(decision.obligations?.retention_days ?? {});
  return (
    <section aria-labelledby="sim-outcome" className={`space-y-4 rounded-lg border-2 ${tone} p-4`}>
      <h3 id="sim-outcome" className="text-lg font-semibold">
        {decision.outcome === "defer" && decision.defer_until
          ? t("compliance.sim.deferred", { when: format.dateTime(decision.defer_until) })
          : t(`compliance.outcome.${decision.outcome}` as MessageId)}
      </h3>
      <p className="text-sm text-[var(--color-muted)]">
        {t("compliance.sim.under", { id: profile.id, version: profile.version })}
        {profile.overrides ? ` ${t("compliance.rules.overridden", { hash: profile.overrides })}` : ""}
      </p>
      {(decision.reasons ?? []).length > 0 && (
        <Hits title={t("compliance.sim.why")} hits={decision.reasons ?? []} sources={sources} />
      )}
      {(decision.warnings ?? []).length > 0 && (
        <Hits title={t("compliance.sim.warnings")} hits={decision.warnings ?? []} sources={sources} />
      )}
      {decision.outcome === "allow" && (
        <div className="space-y-2 text-sm">
          <h4 className="font-medium">{t("compliance.sim.then")}</h4>
          <ul className="list-disc space-y-1 pl-5">
            {plays.length > 0
              ? plays.map((p) => <li key={p.key}>{t("compliance.sim.play", { key: p.key, text: p.text_key, language: p.language })}</li>)
              : <li>{t("compliance.sim.noPlay")}</li>}
            {(decision.obligations?.tools ?? []).length > 0 && (
              <li>{t("compliance.sim.tools", { list: (decision.obligations?.tools ?? []).join(", ") })}</li>
            )}
            {retention.length > 0 && (
              <li>{t("compliance.sim.retention", { list: retention.map(([k, d]) => `${k} ${d}`).join(", ") })}</li>
            )}
          </ul>
        </div>
      )}
    </section>
  );
}

function Hits({ title, hits, sources }: {
  title: string; hits: Schemas["RuleHit"][]; sources: Schemas["Profile"]["sources"] | undefined;
}) {
  return (
    <div className="space-y-2">
      <h4 className="font-medium">{title}</h4>
      <ul className="space-y-3">
        {hits.map((hit, i) => (
          <li key={`${hit.rule}-${i}`} className="space-y-1 text-sm">
            <p><span className="font-mono">{hit.rule}</span>: {hit.detail}</p>
            {sourcesOf(hit.rule, sources).length > 0 && <SourceList sources={sourcesOf(hit.rule, sources)} />}
          </li>
        ))}
      </ul>
    </div>
  );
}
